import type { Variation } from '../models/variation'
import { getRandom } from '../utils/random'
import { getStoredKey } from '../utils/storage'

interface Options<T> {
  variations: Variation<T>[];
}

export class Experiment<T> {
  private readonly variations: Variation<T>[] // Variations for this experiment
  private readonly storageKey: string // Key for storing experiment results in localStorage

  constructor(name: string, options: Options<T>) {
    if (!options.variations || options.variations.length === 0) {
      throw new Error('At least one variation is required')
    }

    const totalPercentage = options.variations.reduce((sum, v) => sum + v.percentage, 0)
    if (totalPercentage !== 100) {
      throw new Error('Total percentage of variations must equal 100')
    }

    this.variations = options.variations
    this.storageKey = getStoredKey(name)
  }

  /**
   * Retrieves the selected variation for the experiment.
   * If results are in localStorage, they are used.
   */
  getVariation(): T {
    const savedValue = localStorage.getItem(this.storageKey)
    if (savedValue) {
      return JSON.parse(savedValue) as T
    }

    const randomValue = getRandom()
    let cumulativePercentage = 0

    for (const variation of this.variations) {
      cumulativePercentage += variation.percentage
      if (randomValue < cumulativePercentage) {
        localStorage.setItem(this.storageKey, JSON.stringify(variation.value))
        return variation.value
      }
    }

    const fallback = this.variations[0].value
    localStorage.setItem(this.storageKey, JSON.stringify(fallback))
    return fallback
  }

  /**
   * Resets the experiment by removing its result from localStorage.
   */
  resetVariation(): void {
    localStorage.removeItem(this.storageKey)
  }

  /**
   * Overrides the variation for this experiment if the provided value is valid.
   *
   * @param value - The value to override the experiment with.
   * @returns True if the override was successful, false otherwise.
   */
  overrideVariation(value: T): boolean {
    const isValid = this.variations.some(variation => variation.value === value)
    if (isValid) {
      localStorage.setItem(this.storageKey, JSON.stringify(value))
      return true
    }
    return false
  }
}
